// hooks/core/useNotifications.ts
import { useState, useEffect, useCallback } from 'react';

interface NotificationAction {
  action: string;
  title: string;
  icon?: string;
}

interface NotificationData {
  title: string;
  body: string;
  icon?: string;
  data?: any;
  actions?: NotificationAction[];  // Pour le SW seulement
  requireInteraction?: boolean;
  tag?: string;
  onClick?: (data: any) => void;
  onAction?: (action: string, data: any) => void;
}

export default function useNotifications() {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isServiceWorkerReady, setIsServiceWorkerReady] = useState(false);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('⚠️ Notifications non supportées');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      console.log('✅ Permission notification:', result);
      return result === 'granted';
    } catch (error) {
      console.error('❌ Erreur permission:', error);
      return false;
    }
  }, []);

  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      console.warn('⚠️ Service Worker non supporté');
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      console.log('✅ Service Worker enregistré', registration);

      await navigator.serviceWorker.ready;
      setIsServiceWorkerReady(true);

      return true;
    } catch (error) {
      console.error('❌ Erreur Service Worker:', error);
      return false;
    }
  }, []);

  const showNotification = useCallback(async (data: NotificationData) => {
    if (Notification.permission !== 'granted') {
      console.warn('⚠️ Permission notification non accordée');
      return false;
    }

    // Priorité 1: Service Worker (supporte les actions)
    if (isServiceWorkerReady && navigator.serviceWorker.controller) {
      try {
        navigator.serviceWorker.controller.postMessage({
          type: 'SHOW_NOTIFICATION',
          title: data.title,
          body: data.body,
          icon: data.icon || '/logo.png',
          data: data.data || {},
          actions: data.actions || [],  // ✅ Passé au SW, pas à Notification API
          requireInteraction: data.requireInteraction || false,
          tag: data.tag || Date.now().toString()
        });
        console.log('✅ Notification envoyée via Service Worker');
        return true;
      } catch (error) {
        console.warn('⚠️ Erreur SW, fallback à Notification API', error);
      }
    }

    // Priorité 2: Notification API (fallback - sans actions)
    try {
      const notification = new Notification(data.title, {
        body: data.body,
        icon: data.icon || '/logo.png',
        badge: '/badge.png',
        requireInteraction: data.requireInteraction || false,
        silent: false,
        tag: data.tag || Date.now().toString()
        // ❌ PAS DE 'actions' ici - l'API Notification ne le supporte pas
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        if (data.onClick) {
          data.onClick(data.data);
        }
      };

      notification.onclose = () => {
        console.log('Notification fermée');
      };

      console.log('✅ Notification envoyée via Notification API');

      if (!data.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 30000);
      }

      return true;
    } catch (error) {
      console.error('❌ Erreur affichage notification:', error);
      return false;
    }
  }, [isServiceWorkerReady]);

  const closeNotifications = useCallback(async () => {
    if (isServiceWorkerReady && navigator.serviceWorker.controller) {
      try {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLOSE_NOTIFICATION'
        });
      } catch (error) {
        console.error('❌ Erreur fermeture notifications SW:', error);
      }
    }
    return true;
  }, [isServiceWorkerReady]);

  const closeNotificationByTag = useCallback(async (tag: string) => {
    if (isServiceWorkerReady && navigator.serviceWorker.controller) {
      try {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLOSE_NOTIFICATION_BY_TAG',
          data: { tag }
        });
      } catch (error) {
        console.error('❌ Erreur fermeture notification:', error);
      }
    }
  }, [isServiceWorkerReady]);

  // Écouter les messages du Service Worker

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const { type, action, payload } = event.data || {};

      if (type === 'NOTIFICATION_ACTION') {
        // Dispatch un événement personnalisé que l'application peut écouter
        window.dispatchEvent(new CustomEvent('notificationAction', {
          detail: { action, payload }
        }));
      }

      if (type === 'NOTIFICATION_CLICK') {
        window.dispatchEvent(new CustomEvent('notificationClick', {
          detail: payload
        }));
      }

      if (type === 'ANSWER_CALL') {
        window.dispatchEvent(new CustomEvent('answerCall', {
          detail: payload
        }));
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
    };
  }, []);


  // Initialisation
  useEffect(() => {
    const init = async () => {
      await requestPermission();
      await registerServiceWorker();
      if ('Notification' in window) {
        setPermission(Notification.permission);
      }
    };

    init();
  }, [requestPermission, registerServiceWorker]);

  return {
    permission,
    isServiceWorkerReady,
    requestPermission,
    registerServiceWorker,
    showNotification,
    closeNotifications,
    closeNotificationByTag
  };
}